import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { useApprovalsController } from 'api/controllers/ApprovalsController';

import { ApprovalRule, ApprovalRuleCreate, User } from 'openapi';

import { AppRoutesEnum } from 'utils/routes';

import { ApprovalRuleForm } from './ApprovalRuleForm';

const transformApprovers = (flow?: {
  steps?: Array<{ approver?: User }>;
}): User[] => {
  if (!flow?.steps) {
    return [];
  }
  const approversList = flow.steps
    .map((step) => step.approver)
    .filter((approver): approver is User => !!approver);
  return approversList;
};

export const EditApprovalRule = () => {
  const { companyId, ruleId } = useParams();
  const [searchParams] = useSearchParams();
  const isFallback = searchParams.get('fallback') === 'true';
  const navigate = useNavigate();
  const {
    getApprovalRule,
    updateApprovalRule,
    getAllApprovalRulesForCompany,
    patchDefaultApprovalRule
  } = useApprovalsController();

  const [ruleData, setRuleData] = useState<ApprovalRule | null>(null);
  const [existingRules, setExistingRules] = useState<ApprovalRule[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      if (!companyId || !ruleId) return;

      try {
        const [ruleResponse, rulesResponse] = await Promise.all([
          getApprovalRule(Number(companyId), Number(ruleId)),
          getAllApprovalRulesForCompany(Number(companyId))
        ]);
        setRuleData(ruleResponse || null);
        setExistingRules(rulesResponse?.rules ?? []);
      } catch (error) {
        console.error('Failed to fetch approval rule data:', error);
      }
    };

    fetchData();
  }, [companyId, ruleId, getApprovalRule, getAllApprovalRulesForCompany]);

  const handleBack = () => {
    if (!companyId) return;
    navigate(
      AppRoutesEnum.COMPANY_APPROVAL_RULES.replace(':companyId', companyId)
    );
  };

  const handleSave = async (values: ApprovalRuleCreate) => {
    if (!companyId || !ruleId) return;

    try {
      if (isFallback) {
        await patchDefaultApprovalRule(Number(companyId), {
          flow: values.flow
        });
      } else {
        await updateApprovalRule(Number(companyId), Number(ruleId), values);
      }
      navigate(
        AppRoutesEnum.COMPANY_APPROVAL_RULES.replace(':companyId', companyId)
      );
    } catch (err) {
      console.error('Error updating approval rule:', err);
    }
  };

  if (!ruleData) {
    return null;
  }

  return companyId ? (
    <ApprovalRuleForm
      initialValues={ruleData}
      selectedMode={ruleData.flow?.approvalMode}
      selectedApprovers={transformApprovers(ruleData?.flow)}
      requiredApprovals={ruleData.flow?.minimumApprovalsRequired || 0}
      exclusive={ruleData.exclusive}
      active={ruleData.active}
      onSave={handleSave}
      onCancel={handleBack}
      companyId={Number(companyId)}
      existingRules={existingRules}
      isFallback={isFallback}
    />
  ) : null;
};
